import { ApolloClient } from '@apollo/client';
import {
  getSubcategories,
  PostMainCategory,
  PostSubcategory,
  PropertyResearchPostCategory,
} from '../../domain/postCategories';
import {
  Content,
  fetchPosts,
  fetchPostsWithAds,
  fetchVideos,
} from './fetchPostsData';
import { PropertyType } from '../../domain/propertyTypes';
import { resolved } from '../../utility/promiseHelper';
import { fifthInEverySix } from '../../ads/adPlacementLogic';

export const fetchPropertyResearchLandingPageData = async (
  client: ApolloClient<unknown>,
  excludePostIds: number[],
) => {
  const propertyResearchLandingPageRequirement = {
    review: 6,
    featuredVideos: 3,
    preview: 3,
    pov: 3,
  };

  const [reviewData, videoData, previewData, pointOfViewData] =
    await Promise.allSettled([
      fetchPostsWithAds(
        client,
        propertyResearchLandingPageRequirement.review,
        0,
        {
          postCategory: [PostSubcategory.Review],
        },
        fifthInEverySix,
        excludePostIds,
      ),
      fetchVideos(
        client,
        propertyResearchLandingPageRequirement.featuredVideos,
        {
          postCategory: getSubcategories(PostMainCategory.ProjectResearch),
        },
      ),
      fetchPosts(
        client,
        propertyResearchLandingPageRequirement.preview,
        0,
        {
          postCategory: [PostSubcategory.Preview],
        },
        excludePostIds,
      ),
      fetchPosts(
        client,
        propertyResearchLandingPageRequirement.pov,
        0,
        {
          postCategory: [PostSubcategory.PointOfView],
        },
        excludePostIds,
      ),
    ]);

  const defaultContents = { contents: [] as Content[], hasMore: false };
  return {
    review: resolved(reviewData, defaultContents).contents,
    video: resolved(videoData, { featuredVideos: [], heroVideo: null }),
    preview: resolved(previewData, defaultContents).contents,
    pointOfView: resolved(pointOfViewData, defaultContents).contents,
  };
};

export const fetchPropertyResearchComboLandingPageData = async (
  client: ApolloClient<unknown>,
  postCategory: PostSubcategory.Preview | PostSubcategory.Review,
  propertyType:
    | PropertyType.Condominium
    | PropertyType.House
    | PropertyType.Townhome,
  excludePostIds: number[],
) => {
  const showVideo = postCategory !== PostSubcategory.Preview;
  const dataRequirement = {
    latest: 6,
    featuredVideos: showVideo ? 3 : 0,
  };

  const [latestData, videoData] = await Promise.allSettled([
    fetchPostsWithAds(
      client,
      dataRequirement.latest,
      0,
      {
        postCategory: [postCategory],
        propertyType,
      },
      fifthInEverySix,
      excludePostIds,
    ),
    fetchVideos(client, dataRequirement.featuredVideos, {
      postCategory: [postCategory],
      propertyType,
    }),
  ]);

  const defaultContents = { contents: [] as Content[], hasMore: false };
  return {
    latest: resolved(latestData, defaultContents).contents,
    video: resolved(videoData, { featuredVideos: [], heroVideo: null }),
  };
};

export const fetchPropertyResearchSubLandingPageData = async (
  client: ApolloClient<unknown>,
  excludePostIds: number[],
  postCategory: PropertyResearchPostCategory,
) => {
  const showVideo = postCategory === 'project_review';
  const propertyResearchSubLandingPageRequirement = {
    condo: 6,
    featuredVideos: showVideo ? 3 : 0,
    house: 6,
    townhome: postCategory === 'point_of_view' ? 0 : 6,
  };

  const [condoData, videoData, houseData, townhomeData] =
    await Promise.allSettled([
      fetchPostsWithAds(
        client,
        propertyResearchSubLandingPageRequirement.condo,
        0,
        {
          postCategory: [postCategory],
          propertyType: PropertyType.Condominium,
        },
        fifthInEverySix,
        excludePostIds,
      ),
      fetchVideos(
        client,
        propertyResearchSubLandingPageRequirement.featuredVideos,
        {
          postCategory: [postCategory],
        },
      ),
      fetchPostsWithAds(
        client,
        propertyResearchSubLandingPageRequirement.house,
        0,
        {
          postCategory: [postCategory],
          propertyType: PropertyType.House,
        },
        fifthInEverySix,
        excludePostIds,
      ),
      fetchPostsWithAds(
        client,
        propertyResearchSubLandingPageRequirement.townhome,
        0,
        {
          postCategory: [postCategory],
          propertyType: PropertyType.Townhome,
        },
        fifthInEverySix,
        excludePostIds,
      ),
    ]);

  const defaultContents = { contents: [] as Content[], hasMore: false };
  return {
    condo: resolved(condoData, defaultContents).contents,
    video: resolved(videoData, { featuredVideos: [], heroVideo: null }),
    house: resolved(houseData, defaultContents).contents,
    townhome: resolved(townhomeData, defaultContents).contents,
  };
};
